import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import { BellRing, Loader2, ShieldAlert, RefreshCw, Volume2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { isSoundEnabled, enableSound } from '../../lib/chime';

const supported = () =>
  typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

const perm = () => (supported() ? Notification.permission : 'granted');

// Non-dismissible pop-up that blocks the app until the user allows push
// notifications AND taps "Allow sound" once (the tap unlocks browser audio so
// the lead bell can ring). It never drops until both are done.
export function PushGate() {
  const { currentUser } = useApp();
  const [status, setStatus] = useState(perm()); // 'default' | 'granted' | 'denied'
  const [soundOk, setSoundOk] = useState(isSoundEnabled());
  const [busy, setBusy] = useState(false);

  const recheck = useCallback(() => setStatus(perm()), []);

  // Keep polling — if the user changes the browser setting, the gate updates.
  useEffect(() => {
    if (!supported() || status === 'granted') return;
    const t = setInterval(recheck, 2000);
    return () => clearInterval(t);
  }, [status, recheck]);

  // If already granted on mount, make sure a subscription exists silently.
  useEffect(() => {
    if (currentUser && supported() && perm() === 'granted') {
      import('../../lib/webPush').then(({ enablePush }) => enablePush(currentUser.id, { prompt: false }));
    }
  }, [currentUser]);

  const granted = status === 'granted';
  const needsSound = supported() && !soundOk;
  if (!currentUser || !supported() || (granted && !needsSound)) return null;

  const allowSound = () => {
    // Must run synchronously inside the click so the browser unlocks audio.
    const rang = enableSound();
    setSoundOk(true);
    toast.success(rang ? 'Sound on — that ring is what you\u2019ll hear for every new lead.' : 'Sound on for lead alerts.');
  };

  const enable = async () => {
    setBusy(true);
    if (!soundOk) { enableSound(); setSoundOk(true); }
    try {
      const { enablePush } = await import('../../lib/webPush');
      const ok = await enablePush(currentUser.id, { prompt: true });
      if (ok) {
        setStatus('granted');
        toast.success("Alerts on — you'll get new leads instantly on this device.");
      } else {
        setStatus(perm());
        if (perm() === 'denied') {
          toast.error('Notifications are blocked. Allow them in your browser, then tap Re-check.');
        } else {
          toast.error('Could not enable alerts. Please try again.');
        }
      }
    } catch {
      toast.error('Could not enable alerts.');
    } finally {
      setBusy(false);
    }
  };

  const denied = status === 'denied';

  const soundRow = (
    <div className="mt-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-left" data-testid="push-gate-sound-row">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${soundOk ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
        {soundOk ? <CheckCircle2 className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800">{soundOk ? 'Sound allowed' : 'Allow lead alert sound'}</p>
        <p className="text-xs text-slate-500">{soundOk ? 'The bell will ring when a new lead arrives.' : 'Browsers keep the page silent until you tap once — tap to allow and hear a test ring.'}</p>
      </div>
      {!soundOk && (
        <Button data-testid="push-gate-sound-btn" size="sm" onClick={allowSound} className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white">
          <Volume2 className="h-4 w-4 mr-1" /> Allow sound
        </Button>
      )}
    </div>
  );

  return (
    <div
      data-testid="push-gate-overlay"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 pointer-events-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        data-testid="push-gate-modal"
        className={`w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200 ${denied ? 'ring-2 ring-red-200' : ''}`}
      >
        <div className={`h-1.5 w-full ${denied ? 'bg-red-500' : 'bg-gradient-to-r from-amber-500 to-emerald-500'}`} />
        <div className="p-6 text-center">
          <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${denied ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
            {denied ? <ShieldAlert className="h-7 w-7" /> : granted ? <Volume2 className="h-7 w-7" /> : <BellRing className="h-7 w-7" />}
          </div>

          {denied ? (
            <>
              <h2 className="text-lg font-bold text-slate-900">Notifications are blocked</h2>
              <p className="mt-2 text-sm text-slate-600">
                You need to allow notifications to receive lead alerts and follow-up reminders. Please unblock them in your browser, then tap <span className="font-semibold">Re-check</span>.
              </p>
              <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3 text-left text-xs text-slate-600 space-y-1">
                <p className="font-semibold text-slate-700">How to unblock:</p>
                <p>1. Tap the lock / site-info icon in your browser's address bar.</p>
                <p>2. Set <span className="font-semibold">Notifications</span> to <span className="font-semibold">Allow</span>.</p>
                <p>3. Come back and tap Re-check below.</p>
              </div>
              {soundRow}
              <Button
                data-testid="push-gate-recheck-btn"
                onClick={recheck}
                className="mt-5 w-full bg-slate-800 hover:bg-slate-900 text-white"
              >
                <RefreshCw className="h-4 w-4 mr-1.5" /> Re-check
              </Button>
            </>
          ) : granted ? (
            <>
              <h2 className="text-lg font-bold text-slate-900">Allow lead alert sound</h2>
              <p className="mt-2 text-sm text-slate-600">
                Notifications are on. One more tap lets this device ring the bell when a new lead arrives.
              </p>
              <Button
                data-testid="push-gate-sound-only-btn"
                onClick={allowSound}
                className="mt-5 w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Volume2 className="h-4 w-4 mr-1.5" /> Allow sound &amp; play test ring
              </Button>
              <p className="mt-3 text-[11px] text-slate-400">You can mute it anytime from the bell menu.</p>
            </>
          ) : (
            <>
              <h2 className="text-lg font-bold text-slate-900">Turn on notifications &amp; sound</h2>
              <p className="mt-2 text-sm text-slate-600">
                Allow alerts to get new leads the moment they arrive and never miss a follow-up reminder — even when the app is closed. Lead alerts also ring a bell on this device.
              </p>
              {soundRow}
              <Button
                data-testid="push-gate-enable-btn"
                onClick={enable}
                disabled={busy}
                className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><BellRing className="h-4 w-4 mr-1.5" /> Enable Notifications{soundOk ? '' : ' & Sound'}</>}
              </Button>
              <p className="mt-3 text-[11px] text-slate-400">This message stays until notifications and sound are enabled.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
